import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusTimeline";
import { meta } from "@/lib/statusMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, Plus, Search, Truck, Store, Package, Loader2 } from "lucide-react";

const FILTERS = ["all", "received", "processing", "scanning", "printing", "ready_for_pickup", "shipped"];

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, by_status: {} });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q) params.q = q;
      const [o, s] = await Promise.all([api.get("/orders", { params }), api.get("/stats")]);
      setOrders(o.data);
      setStats(s.data);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/square/sync");
      if (res.data.ok === false) {
        toast.error(res.data.error || "Square sync failed");
      } else {
        toast.success(`Square sync: ${res.data.created} lab order(s) imported${res.data.skipped ? `, ${res.data.skipped} non-lab skipped` : ""}`);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Square sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">Lab Queue</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={sync} disabled={syncing} data-testid="square-sync-btn">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Sync Square</span>
          </Button>
          <NewOrderDialog onCreated={load} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Orders" value={stats.total} icon={Package} />
        <StatCard label="Active in Lab" value={stats.active} icon={Loader2} accent />
        <StatCard label="Ready / Pickup" value={stats.by_status?.ready_for_pickup || 0} color="#10B981" icon={Store} />
        <StatCard label="Shipped" value={stats.by_status?.shipped || 0} color="#059669" icon={Truck} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, name, email…"
            className="pl-9" data-testid="order-search-input" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                filter === f ? "bg-brand text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}>
              {f === "all" ? "All" : meta(f).label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center text-muted-foreground mono text-sm animate-pulse">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground">No orders match this view.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="orders-grid">
          {orders.map((o) => <OrderCard key={o.id} o={o} />)}
        </div>
      )}
    </Layout>
  );
}

function StatCard({ label, value, icon: Icon, color, accent }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color: color || (accent ? "#6BAE7C" : "hsl(var(--muted-foreground))") }} />
      </div>
      <div className="font-heading text-3xl font-bold" style={{ color: color || undefined }}>{value}</div>
    </div>
  );
}

function OrderCard({ o }) {
  const isPickup = o.fulfillment_type === "pickup";
  return (
    <Link to={`/orders/${o.id}`} data-testid={`order-card-${o.order_number.replace("#", "")}`}
      className="group bg-card border border-border rounded-xl p-5 flex flex-col gap-4 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-brand/50">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="mono text-sm font-semibold group-hover:text-brand transition-colors">{o.order_number}</div>
          <div className="text-sm text-muted-foreground mt-0.5">{o.customer_name}</div>
        </div>
        <StatusBadge status={o.status} />
      </div>
      <div className="text-xs text-muted-foreground space-y-1">
        {o.items?.slice(0, 2).map((it, i) => (
          <div key={i} className="truncate">{it.qty}× {it.title}{it.variant ? ` · ${it.variant}` : ""}</div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-border">
        <span className="flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider text-muted-foreground">
          {isPickup ? <Store className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
          {isPickup ? "Pickup" : "Ship"}
        </span>
        <div className="flex gap-1">
          {o.service_scanning && <Tag>Scan</Tag>}
          {o.service_printing && <Tag>Print</Tag>}
        </div>
      </div>
    </Link>
  );
}

const Tag = ({ children }) => (
  <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground uppercase tracking-wider">{children}</span>
);

function NewOrderDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    customer_name: "", customer_email: "", customer_phone: "", fulfillment_type: "shipped",
    service_scanning: true, service_printing: false, item_title: "", item_variant: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.customer_name || !f.customer_email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      await api.post("/orders", {
        customer_name: f.customer_name, customer_email: f.customer_email,
        customer_phone: f.customer_phone,
        fulfillment_type: f.fulfillment_type, service_scanning: f.service_scanning,
        service_printing: f.service_printing,
        items: f.item_title ? [{ title: f.item_title, variant: f.item_variant, qty: 1 }] : [],
      });
      toast.success("Order created — confirmation email sent");
      setOpen(false);
      setF({ customer_name: "", customer_email: "", customer_phone: "", fulfillment_type: "shipped", service_scanning: true, service_printing: false, item_title: "", item_variant: "" });
      onCreated();
    } catch { toast.error("Failed to create order"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="new-order-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Order
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border">
        <DialogHeader><DialogTitle className="font-heading">Create manual order</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer name</Label>
              <Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="new-order-name" className="mt-1.5" /></div>
            <div><Label>Email</Label>
              <Input value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="new-order-email" className="mt-1.5" /></div>
          </div>
          <div><Label>Phone <span className="text-muted-foreground">(for pickup SMS, E.164 e.g. +1…)</span></Label>
            <Input value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+15551234567" data-testid="new-order-phone" className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Film / item</Label>
              <Input value={f.item_title} onChange={(e) => set("item_title", e.target.value)} placeholder="Kodak Portra 400" className="mt-1.5" /></div>
            <div><Label>Format / service</Label>
              <Input value={f.item_variant} onChange={(e) => set("item_variant", e.target.value)} placeholder="35mm C-41 + Scans" className="mt-1.5" /></div>
          </div>
          <div><Label>Fulfillment</Label>
            <Select value={f.fulfillment_type} onValueChange={(v) => set("fulfillment_type", v)}>
              <SelectTrigger className="mt-1.5" data-testid="new-order-fulfillment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="shipped">Ship to customer</SelectItem>
                <SelectItem value="pickup">In-store pickup</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.service_scanning} onCheckedChange={(v) => set("service_scanning", v)} data-testid="new-order-scan" /> Scanning</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.service_printing} onCheckedChange={(v) => set("service_printing", v)} data-testid="new-order-print" /> Printing</label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="new-order-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
